// src/pages/farmer/farmerpanel/BrowsePanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';

const CATEGORIES = ['All', 'Goat', 'Sheep', 'Bakra', 'Other'];

export default function BrowsePanel({ user }) {
  const [animals,   setAnimals]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [category,  setCategory]  = useState('All');
  const [minPrice,  setMinPrice]  = useState('');
  const [maxPrice,  setMaxPrice]  = useState('');
  const [selected,  setSelected]  = useState(null);
  const [inquiryAnimal, setInquiryAnimal] = useState(null);
  const [inquiryMsg,    setInquiryMsg]    = useState('');
  const [inquirySent,   setInquirySent]   = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [inquiryError, setInquiryError] = useState('');

  const fetchAnimals = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (category !== 'All') params.category = category;
      if (search)    params.search    = search;
      if (minPrice)  params.min_price = minPrice;
      if (maxPrice)  params.max_price = maxPrice;

      const { data } = await api.get('/api/public/browse', { params });
      // Other farmers' listings only — you can't send an inquiry about your own animal
      setAnimals(data.filter(a => a.seller_id !== user?.id));
    } catch {
      setError('Could not load listings. Please try again.');
    } finally { setLoading(false); }
  }, [category, search, minPrice, maxPrice, user?.id]);

  useEffect(() => { fetchAnimals(); }, [category, fetchAnimals]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAnimals();
  };

  const handleSendInquiry = async () => {
    if (!inquiryMsg.trim()) return;
    setInquiryLoading(true);
    setInquiryError('');
    try {
      await api.post('/api/farmer/inquiries', {
        animal_id: inquiryAnimal.id,
        message:   inquiryMsg,
      });
      setInquirySent(true);
      setInquiryMsg('');
    } catch (err) {
      setInquiryError(err.response?.data?.error || 'Could not send inquiry.');
    } finally { setInquiryLoading(false); }
  };

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-black text-on-surface">Browse Other Farmers' Listings</h2>
        <p className="text-sm text-secondary mt-1">
          Send an inquiry about animals listed by other farmers on the platform.
        </p>
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-outline-variant/10">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">
              Search
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2
                               text-outline text-[18px]">search</span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Name, breed, description..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-outline-variant/40
                           text-sm outline-none focus:border-primary focus:ring-2
                           focus:ring-primary/10 transition-all" />
            </div>
          </div>

          <div className="w-32">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">
              Min ₹
            </label>
            <input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2.5 rounded-xl border border-outline-variant/40
                         text-sm outline-none focus:border-primary transition-all" />
          </div>

          <div className="w-32">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">
              Max ₹
            </label>
            <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)}
              placeholder="Any"
              className="w-full px-3 py-2.5 rounded-xl border border-outline-variant/40
                         text-sm outline-none focus:border-primary transition-all" />
          </div>

          <button type="submit"
            className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-bold
                       hover:opacity-90 transition-all">
            Search
          </button>
          <button type="button" onClick={() => {
              setSearch(''); setMinPrice(''); setMaxPrice('');
              setCategory('All'); setTimeout(fetchAnimals, 50);
            }}
            className="px-4 py-2.5 rounded-xl border border-outline-variant text-secondary
                       text-sm font-semibold hover:bg-surface-container transition-all">
            Reset
          </button>
        </form>

        <div className="flex gap-2 mt-3 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                category === c
                  ? 'bg-primary text-white'
                  : 'bg-surface-container text-secondary hover:bg-surface-container-high'
              }`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3
                        rounded-xl text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-base">error</span>
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm
                                    border border-outline-variant/10 animate-pulse">
              <div className="h-48 bg-surface-container" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-surface-container rounded w-3/4" />
                <div className="h-3 bg-surface-container rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : animals.length === 0 ? (
        <div className="bg-white rounded-2xl p-14 shadow-sm border border-outline-variant/10
                        flex flex-col items-center text-center gap-3">
          <span className="material-symbols-outlined text-[48px] text-outline">
            sentiment_dissatisfied
          </span>
          <p className="text-lg font-black text-on-surface">No listings found</p>
          <p className="text-sm text-secondary">Try adjusting your filters or search term.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-secondary font-semibold">
            {animals.length} listing{animals.length !== 1 ? 's' : ''} found
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {animals.map(a => (
              <div key={a.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border
                           border-outline-variant/10 hover:shadow-md hover:border-primary/20
                           transition-all cursor-pointer group"
                onClick={() => setSelected(a)}>
                <div className="h-48 bg-surface-container overflow-hidden relative">
                  {a.image_url ? (
                    <img src={a.image_url.startsWith('/uploads')
                                ? `http://localhost:4000${a.image_url}`
                                : a.image_url}
                         alt={a.name}
                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-[48px] text-outline">pets</span>
                    </div>
                  )}
                  <span className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm
                                   text-[10px] font-black px-2 py-1 rounded-full text-primary">
                    {a.category}
                  </span>
                  {a.status === 'sold' && (
                    <span className="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                      Sold Out
                    </span>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start gap-2">
                    <p className="font-black text-sm text-on-surface leading-tight">{a.name}</p>
                    <p className="text-primary font-black text-sm whitespace-nowrap">
                      ₹{Number(a.price_inr).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <p className="text-xs text-secondary mt-1">{a.breed || 'Mixed breed'}</p>
                  <p className="text-xs text-secondary mt-1">by {a.seller_name || 'Farmer'}</p>

                  <div className="flex gap-3 mt-2 text-xs text-secondary">
                    {a.weight_kg && (
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[13px]">scale</span>
                        {a.weight_kg}kg
                      </span>
                    )}
                    {a.location_city && (
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[13px]">location_on</span>
                        {a.location_city}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={e => { e.stopPropagation(); setInquiryAnimal(a); setInquirySent(false); setInquiryError(''); }}
                    className="w-full mt-3 py-2 rounded-xl text-xs font-bold bg-primary text-white
                              hover:opacity-90 transition-all flex items-center justify-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">forum</span>
                    Send Inquiry
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>

    {/* Detail Modal */}
    {selected && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-3"
           onClick={() => setSelected(null)}>
        <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl flex flex-col" style={{maxHeight:'90vh'}}
             onClick={e => e.stopPropagation()}>
          <div className="h-32 sm:h-48 bg-surface-container relative rounded-t-2xl overflow-hidden flex-shrink-0">
            {selected.image_url ? (
              <img src={selected.image_url.startsWith('/uploads')
                          ? `http://localhost:4000${selected.image_url}`
                          : selected.image_url}
                   alt={selected.name}
                   className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="material-symbols-outlined text-[64px] text-outline">pets</span>
              </div>
            )}
            <button onClick={() => setSelected(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40
                         text-white flex items-center justify-center hover:bg-black/60">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>

          <div className="p-3 space-y-2 overflow-y-auto flex-1">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-sm font-black text-on-surface">{selected.name}</h3>
                <p className="text-xs text-secondary">{selected.breed} · {selected.category}</p>
              </div>
              <p className="text-base font-black text-primary">
                ₹{Number(selected.price_inr).toLocaleString('en-IN')}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              {[
                { icon: 'scale',       label: 'Weight',  val: selected.weight_kg   ? `${selected.weight_kg}kg`      : '—' },
                { icon: 'cake',        label: 'Age',     val: selected.age_months   ? `${selected.age_months} mo`    : '—' },
                { icon: 'dentistry',   label: 'Teeth',   val: selected.teeth_count  ? `${selected.teeth_count}`      : '—' },
                { icon: 'location_on', label: 'City',    val: selected.location_city  || '—' },
                { icon: 'map',         label: 'State',   val: selected.location_state || '—' },
                { icon: 'vaccines',    label: 'Health',  val: selected.health_status  || '—' },
              ].map(({ icon, label, val }) => (
                <div key={label} className="bg-surface-container-low rounded-lg p-1.5 text-center">
                  <span className="material-symbols-outlined text-primary text-[14px]">{icon}</span>
                  <p className="text-[8px] text-secondary font-bold uppercase">{label}</p>
                  <p className="text-[10px] font-black text-on-surface truncate">{val}</p>
                </div>
              ))}
            </div>

            {selected.description && (
              <div className="bg-surface-container-low rounded-lg p-2">
                <p className="text-[9px] font-black uppercase text-secondary mb-0.5">Description</p>
                <p className="text-xs text-on-surface">{selected.description}</p>
              </div>
            )}

            {selected.seller_name && (
              <div className="flex items-center gap-2 bg-emerald-50 rounded-lg p-2">
                <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center
                                justify-center text-white font-black text-xs">
                  {selected.seller_name[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-black text-on-surface">{selected.seller_name}</p>
                  <p className="text-[10px] text-secondary">Verified Farmer</p>
                </div>
              </div>
            )}

            <button
              onClick={() => { setInquiryAnimal(selected); setInquirySent(false); setInquiryError(''); }}
              className="w-full py-2 text-xs rounded-xl bg-primary text-white font-bold
                        hover:opacity-90 transition-all flex items-center justify-center gap-1">
              <span className="material-symbols-outlined text-[14px]">forum</span>
              Send Inquiry
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Inquiry Modal */}
    {inquiryAnimal && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => { setInquiryAnimal(null); setInquirySent(false); }}>
        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4"
            onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-black text-lg">Send Inquiry</h3>
              <p className="text-sm text-secondary">{inquiryAnimal.name}</p>
            </div>
            <button onClick={() => { setInquiryAnimal(null); setInquirySent(false); }}
              className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>

          {inquirySent ? (
            <div className="text-center py-6 space-y-2">
              <span className="material-symbols-outlined text-[48px] text-emerald-500">check_circle</span>
              <p className="font-black text-on-surface">Inquiry Sent!</p>
              <p className="text-sm text-secondary">The farmer will reply soon.</p>
              <button onClick={() => { setInquiryAnimal(null); setInquirySent(false); }}
                className="mt-2 px-6 py-2 rounded-xl bg-primary text-white font-bold text-sm">
                Done
              </button>
            </div>
          ) : (
            <>
              {inquiryError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs">
                  {inquiryError}
                </div>
              )}
              <textarea
                value={inquiryMsg}
                onChange={e => setInquiryMsg(e.target.value)}
                placeholder="Ask about age, health, delivery, price negotiation..."
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-outline-variant/40 text-sm
                          outline-none focus:border-primary focus:ring-2 focus:ring-primary/10
                          resize-none transition-all" />
              <button
                onClick={handleSendInquiry}
                disabled={inquiryLoading || !inquiryMsg.trim()}
                className="w-full py-3 rounded-xl bg-primary text-white font-bold
                          hover:opacity-90 disabled:opacity-50 transition-all">
                {inquiryLoading ? 'Sending...' : 'Send Message'}
              </button>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}
